import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErrorMessage, formatINR } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { REPORTS } from "@/constants/testIds";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartLineUp, ArrowLeft, TrendUp, WhatsappLogo, BellRinging } from "@phosphor-icons/react";

const COLORS = {
  pending: "#F59E0B",
  overdue: "#F43F5E",
  recovered: "#10B981",
};

function formatMonth(m) {
  if (!m || m === "unknown") return m;
  const [y, mo] = m.split("-");
  const date = new Date(Number(y), Number(mo) - 1, 1);
  return date.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

function tooltipStyle() {
  return {
    background: "#0e0e11",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 12,
    color: "#F3F4F6",
    fontSize: 12,
  };
}

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports");
        setData(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <div className="p-10 text-neutral-500 text-sm">Loading reports…</div>
      </div>
    );
  }
  if (!data) return null;

  const timeseries = (data.timeseries || []).map((t) => ({
    ...t,
    label: formatMonth(t.month),
  }));

  const splitData = [
    { name: "Pending", value: data.status_split.pending, key: "pending" },
    { name: "Overdue", value: data.status_split.overdue, key: "overdue" },
    { name: "Recovered", value: data.status_split.recovered, key: "recovered" },
  ].filter((d) => d.value > 0);

  const totalAmount =
    (data.status_split.pending || 0) +
    (data.status_split.overdue || 0) +
    (data.status_split.recovered || 0);

  return (
    <div data-testid={REPORTS.root} className="min-h-screen flex flex-col">
      <AppHeader />
      <main className="flex-1 mx-auto max-w-6xl px-5 md:px-8 py-8 md:py-12 w-full">
        <Link
          to="/"
          className="text-sm text-neutral-500 hover:text-neutral-200 flex items-center gap-1.5 mb-6"
        >
          <ArrowLeft size={14} weight="bold" /> Back to dashboard
        </Link>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
              Reports
            </div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight">
              Your lending health
            </h1>
            <p className="text-neutral-400 mt-2 text-sm">
              {data.totals.loans_count} loan
              {data.totals.loans_count === 1 ? "" : "s"} · {data.totals.recovered_count}{" "}
              recovered · {data.totals.overdue_count} overdue
            </p>
          </div>
        </div>

        {/* Timeseries area chart */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="lele-card p-6 md:p-8 mb-6"
          data-testid={REPORTS.timeseriesChart}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-2">
                <TrendUp size={13} weight="fill" className="text-indigo-400" />
                Money flow over time
              </div>
              <div className="font-heading text-xl mt-1">Lent vs. recovered</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            {timeseries.length === 0 ? (
              <div className="grid place-items-center h-full text-neutral-500 text-sm">
                No data yet — create loans and log payments to see trends.
              </div>
            ) : (
              <ResponsiveContainer>
                <AreaChart data={timeseries}>
                  <defs>
                    <linearGradient id="lentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="recGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="#6B7280"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#6B7280"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) =>
                      v >= 100000
                        ? `${(v / 100000).toFixed(1)}L`
                        : v >= 1000
                        ? `${(v / 1000).toFixed(0)}k`
                        : v
                    }
                  />
                  <Tooltip
                    contentStyle={tooltipStyle()}
                    formatter={(v, name) => [formatINR(v), name]}
                    cursor={{ stroke: "rgba(99,102,241,0.2)", strokeWidth: 1 }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, color: "#9CA3AF" }}
                    iconType="circle"
                  />
                  <Area
                    type="monotone"
                    dataKey="lent"
                    name="Lent"
                    stroke="#6366F1"
                    strokeWidth={2}
                    fill="url(#lentGrad)"
                  />
                  <Area
                    type="monotone"
                    dataKey="recovered"
                    name="Recovered"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#recGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </motion.div>

        {/* Two-column: status split + top borrowers */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="lele-card p-6 md:p-8 lg:col-span-2"
            data-testid={REPORTS.splitChart}
          >
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-1">
              Current status
            </div>
            <div className="font-heading text-xl mb-4">Money by state</div>
            <div style={{ width: "100%", height: 240 }}>
              {splitData.length === 0 ? (
                <div className="grid place-items-center h-full text-neutral-500 text-sm">
                  No active balances.
                </div>
              ) : (
                <ResponsiveContainer>
                  <PieChart>
                    <Tooltip
                      contentStyle={tooltipStyle()}
                      formatter={(v) => formatINR(v)}
                    />
                    <Pie
                      data={splitData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      stroke="none"
                    >
                      {splitData.map((entry) => (
                        <Cell key={entry.key} fill={COLORS[entry.key]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              {["pending", "overdue", "recovered"].map((k) => (
                <div key={k} className="rounded-xl border border-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    {k}
                  </div>
                  <div
                    className="font-heading text-sm mt-1"
                    style={{ color: COLORS[k] }}
                  >
                    {formatINR(data.status_split[k])}
                  </div>
                </div>
              ))}
            </div>
            {totalAmount === 0 && (
              <div className="mt-3 text-[11px] text-neutral-500 text-center">
                No balances tracked yet.
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="lele-card p-6 md:p-8 lg:col-span-3"
            data-testid={REPORTS.topBorrowersList}
          >
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-1 flex items-center gap-2">
              <ChartLineUp size={13} weight="fill" className="text-amber-400" />
              Top outstanding
            </div>
            <div className="font-heading text-xl mb-4">Who owes you most</div>
            {data.top_borrowers.length === 0 ? (
              <div className="text-neutral-500 text-sm">
                Everyone's paid up! Nothing outstanding.
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.top_borrowers.map((b, i) => {
                  const waHref = b.phone
                    ? `https://wa.me/${b.phone.replace(/\D/g, "")}`
                    : null;
                  return (
                    <li
                      key={`${b.name}-${i}`}
                      className="py-3 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-neutral-100 font-medium truncate">
                          {b.name}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {b.loans} loan{b.loans === 1 ? "" : "s"}
                          {b.phone ? ` · ${b.phone}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <div className="font-heading text-lg text-amber-300">
                            {formatINR(b.outstanding)}
                          </div>
                          <div className="text-[11px] text-neutral-500">
                            outstanding
                          </div>
                        </div>
                        {waHref && (
                          <a
                            href={waHref}
                            target="_blank"
                            rel="noreferrer"
                            className="lele-btn-ghost !py-2 !px-3 flex items-center gap-1 text-xs"
                            style={{
                              borderColor: "rgba(37,211,102,0.35)",
                              color: "#25D366",
                            }}
                          >
                            <WhatsappLogo size={14} weight="fill" /> Ping
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        </div>

        {/* Reminder analytics */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="lele-card p-6 md:p-8 lg:col-span-3"
            data-testid={REPORTS.remindersChart}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-2">
                  <BellRinging size={13} weight="fill" className="text-amber-400" />
                  Reminder activity
                </div>
                <div className="font-heading text-xl mt-1">
                  Reminders sent over time
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                  All-time total
                </div>
                <div className="font-heading text-2xl text-amber-300">
                  {data.reminders?.total ?? 0}
                </div>
              </div>
            </div>
            <div style={{ width: "100%", height: 240 }}>
              {(!data.reminders || data.reminders.timeseries.length === 0) ? (
                <div className="grid place-items-center h-full text-neutral-500 text-sm text-center px-4">
                  No reminders sent yet. Nudge a borrower from any pending loan
                  to see the trend here.
                </div>
              ) : (
                <ResponsiveContainer>
                  <BarChart
                    data={data.reminders.timeseries.map((r) => ({
                      ...r,
                      label: formatMonth(r.month),
                    }))}
                  >
                    <defs>
                      <linearGradient id="remGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.35} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      stroke="#6B7280"
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#6B7280"
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle()}
                      cursor={{ fill: "rgba(245,158,11,0.08)" }}
                      formatter={(v) => [v, "Reminders"]}
                    />
                    <Bar
                      dataKey="count"
                      fill="url(#remGrad)"
                      radius={[6, 6, 0, 0]}
                      barSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="lele-card p-6 md:p-8 lg:col-span-2"
            data-testid={REPORTS.topRemindedList}
          >
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-1 flex items-center gap-2">
              <BellRinging size={13} weight="fill" className="text-amber-400" />
              Needs the most nudging
            </div>
            <div className="font-heading text-xl mb-4">
              Top reminded borrowers
            </div>
            {(!data.reminders || data.reminders.top_reminded.length === 0) ? (
              <div className="text-neutral-500 text-sm">
                No reminders logged yet.
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.reminders.top_reminded.map((b, i) => {
                  const waHref = b.phone
                    ? `https://wa.me/${b.phone.replace(/\D/g, "")}`
                    : null;
                  return (
                    <li
                      key={`${b.name}-${i}`}
                      className="py-3 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-neutral-100 font-medium truncate">
                          {b.name}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {b.count} reminder{b.count === 1 ? "" : "s"}
                          {b.outstanding > 0
                            ? ` · ${formatINR(b.outstanding)} outstanding`
                            : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="font-heading text-lg text-amber-300">
                          {b.count}
                        </div>
                        {waHref && (
                          <a
                            href={waHref}
                            target="_blank"
                            rel="noreferrer"
                            className="lele-btn-ghost !py-2 !px-3 flex items-center gap-1 text-xs"
                            style={{
                              borderColor: "rgba(37,211,102,0.35)",
                              color: "#25D366",
                            }}
                          >
                            <WhatsappLogo size={14} weight="fill" /> Ping
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
